import React, { useEffect, useState } from "react";
import { Bell, BellOff, BellRing, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const PushNotificationsToggle = () => {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    setPermission(Notification.permission);
    navigator.serviceWorker.getRegistration("/service-worker.js").then((reg) => {
      if (!reg) return;
      reg.pushManager.getSubscription().then((s) => setSubscribed(!!s));
    });
  }, []);

  const enable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js");
      await navigator.serviceWorker.ready;
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permesso negato", { description: "Abilita le notifiche dalle impostazioni del browser." });
        return;
      }
      const { data } = await api.get("/push/public-key");
      if (!data.public_key) throw new Error("Server non configurato per push");
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
      await api.post("/push/subscribe", {
        subscription: sub.toJSON(),
        user_agent: navigator.userAgent,
      });
      setSubscribed(true);
      toast.success("Notifiche attivate", { description: "Riceverai un avviso quando arriverà un nuovo ordine." });
    } catch (e) {
      toast.error("Attivazione fallita", { description: e?.message || String(e) });
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/service-worker.js");
      const sub = reg && (await reg.pushManager.getSubscription());
      if (sub) {
        await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
        await sub.unsubscribe();
      }
      setSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (e) {
      toast.error("Errore", { description: e?.message || String(e) });
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/push/test");
      const { sent, failed, errors, subscriptions } = r.data || {};
      if (failed > 0) {
        toast.error("Push non consegnata", {
          description: `Inviata a ${sent}/${subscriptions} dispositivi. Errore: ${(errors && errors[0]) || "sconosciuto"}`,
          duration: 8000,
        });
      } else {
        toast.success("Notifica di test inviata", { description: `${sent} dispositivo/i raggiunto/i. Se non la vedi: chiudi e riapri l'app, controlla Impostazioni > Notifiche > EasyIce.` });
      }
    } catch (e) {
      toast.error("Test fallito", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setTesting(false);
    }
  };

  if (!supported) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="push-not-supported">
        <BellOff className="w-4 h-4 inline mr-2" />
        Le notifiche push non sono supportate su questo browser. Installa l'app dalla pagina <strong>/installa</strong> per attivarle su iPhone.
      </div>
    );
  }

  return (
    <div className="bg-white border border-sky-100 rounded-xl p-4 flex items-center gap-3 flex-wrap" data-testid="push-toggle">
      <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${subscribed ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
        {subscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-[180px]">
        <p className="text-sm font-medium text-[#032B44]">
          {subscribed ? "Notifiche push attive" : "Notifiche push disattivate"}
        </p>
        <p className="text-xs text-slate-500">
          {subscribed ? "Riceverai un avviso quando arrivano ordini, anche con app chiusa." : "Attivale per ricevere notifiche di nuovi ordini sul telefono."}
        </p>
      </div>
      {subscribed && (
        <button onClick={sendTest} disabled={testing} className="h-9 px-3 rounded-lg border border-sky-200 text-[#032B44] text-xs font-medium hover:bg-sky-50 transition-colors disabled:opacity-50" data-testid="push-test-button">
          {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Test"}
        </button>
      )}
      <button
        onClick={subscribed ? disable : enable}
        disabled={loading || permission === "denied"}
        className={`h-9 px-4 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
          subscribed
            ? "border border-red-200 text-red-700 hover:bg-red-50"
            : "bg-[#032B44] hover:bg-[#00A3FF] text-white"
        }`}
        data-testid="push-toggle-button"
      >
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : subscribed ? "Disattiva" : "Attiva"}
      </button>
      {permission === "denied" && (
        <p className="basis-full text-xs text-red-600 mt-1">
          Permesso bloccato dal browser. Vai nelle impostazioni del sito ({window.location.host}) e consenti le notifiche.
        </p>
      )}
    </div>
  );
};

export default PushNotificationsToggle;
